import { useEffect, useMemo } from 'react';
import { useGmailInboxes, useMyAccess, type GmailInbox } from './queries';
import {
  can as canWorkspace,
  canDoAction,
  canInAnyInbox,
  canInInbox,
  parseAccess,
  type AccessPayload,
  type ActionContext,
} from './permissions';

// The signed-in member's permissions, for hiding and disabling UI.
//
// The data is the database's own answer (my_access RPC) and refetches every
// minute and on focus, so a demotion or an expiry shows up almost immediately,
// unlike the old localStorage-cached team_member.role. Enforcement is never
// here: the server refuses the action whatever the UI shows.
//
// To avoid the nav and buttons flashing on every full page load, the last
// answer for this tab is kept in sessionStorage and used as a placeholder
// while the real one loads. It is only ever a placeholder: the query still
// fetches immediately and replaces it.

const SNAPSHOT_KEY = 'pa-access-snapshot';

function readSnapshot(): AccessPayload | null {
  try {
    const raw = window.sessionStorage.getItem(SNAPSHOT_KEY);
    return raw ? parseAccess(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function writeSnapshot(a: AccessPayload | null) {
  try {
    if (a) window.sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(a));
    else window.sessionStorage.removeItem(SNAPSHOT_KEY);
  } catch {
    // private window / storage blocked: the placeholder is only a nicety
  }
}

export interface UseAccess {
  /** null while loading, and for a non-member. */
  access: AccessPayload | null;
  /** True until the first real answer (a stored snapshot does not count). */
  isLoading: boolean;
  isError: boolean;
  isOwner: boolean;
  roleName: string | null;
  /** A workspace permission. */
  can: (key: string) => boolean;
  /** An inbox permission in one inbox. */
  canIn: (inboxId: string | null | undefined, key: string) => boolean;
  /** An inbox permission anywhere (compose has no inbox yet). */
  canAny: (key: string) => boolean;
  /** A ticket action (claim, resolve, merge…) in the ticket's inbox. */
  canAction: (inboxId: string | null | undefined, action: string, ctx: ActionContext) => boolean;
}

/**
 * Active inboxes where the caller holds `permission` (default: can open it).
 * gmail_inboxes RLS also shows rows to people who only manage inboxes, so the
 * switcher, the /inbox/:slug guard and compose all filter through here.
 * If my_access itself failed, fall back to what RLS returned: the server
 * still refuses anything not allowed.
 */
export function useViewableInboxes(permission = 'tickets.view'): {
  data: GmailInbox[];
  isLoading: boolean;
} {
  const inboxesQ = useGmailInboxes();
  const a = useAccess();
  const data = useMemo(() => {
    const all = inboxesQ.data ?? [];
    if (!a.access && a.isError) return all;
    return all.filter((i) => a.canIn(i.id, permission));
  }, [inboxesQ.data, a, permission]);
  return { data, isLoading: inboxesQ.isLoading || (a.isLoading && !a.access) };
}

export function useAccess(): UseAccess {
  const q = useMyAccess();
  const live = q.data ?? null;

  useEffect(() => {
    if (q.isSuccess) writeSnapshot(live);
  }, [q.isSuccess, live]);

  // A stored snapshot is used only until the first real answer arrives.
  const access = useMemo(
    () => (q.isSuccess ? live : q.isLoading ? readSnapshot() : live),
    [q.isSuccess, q.isLoading, live],
  );

  return useMemo<UseAccess>(
    () => ({
      access,
      isLoading: q.isLoading,
      isError: q.isError,
      isOwner: !!access?.workspace_role.is_owner,
      roleName: access?.workspace_role.name ?? null,
      can: (key) => canWorkspace(access, key),
      canIn: (inboxId, key) => canInInbox(access, inboxId, key),
      canAny: (key) => canInAnyInbox(access, key),
      canAction: (inboxId, action, ctx) => canDoAction(access, inboxId, action, ctx),
    }),
    [access, q.isLoading, q.isError],
  );
}
